// src/components/Logout.js
import React, { useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaSignOutAlt, FaSignInAlt, FaHome } from 'react-icons/fa';
import { Helmet } from 'react-helmet';

function Logout() {
    useEffect(() => {
        const storedUser = localStorage.getItem('user');

        if (storedUser) {
            localStorage.removeItem('user');
            window.location.href = "/";
        } else {
            window.location.href = "/";
        }
    }, []);

    const styles = {
        container: {
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
            padding: '2rem',
        },
        card: {
            background: '#ffffff',
            borderRadius: '20px',
            padding: '3rem 2rem',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
            maxWidth: '600px',
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
        },
        iconContainer: {
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, #4299E1 0%, #2B6CB0 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 2rem',
            boxShadow: '0 10px 20px rgba(66, 153, 225, 0.2)',
        },
        icon: {
            fontSize: '2.5rem',
            color: '#ffffff',
        },
        title: {
            fontSize: '2.5rem',
            fontWeight: '700',
            color: '#2D3748',
            marginBottom: '1.5rem',
            textAlign: 'center',
            background: 'linear-gradient(135deg, #2D3748 0%, #4A5568 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
        },
        message: {
            fontSize: '1.2rem',
            color: '#4A5568',
            marginBottom: '2.5rem',
            textAlign: 'center',
            lineHeight: '1.6',
        },
        buttonContainer: {
            display: 'flex',
            justifyContent: 'center',
            gap: '1rem',
            marginTop: '1rem',
        },
        button: {
            padding: '0.8rem 2rem',
            fontSize: '1.1rem',
            fontWeight: '500',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            border: 'none',
        },
        primaryButton: {
            background: 'linear-gradient(135deg, #4299E1 0%, #2B6CB0 100%)',
            color: '#ffffff',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 16px rgba(66, 153, 225, 0.2)',
            },
        },
        secondaryButton: {
            background: '#EDF2F7',
            color: '#4A5568',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
            },
        },
        decorativeCircle: {
            position: 'absolute',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(66, 153, 225, 0.1) 0%, rgba(43, 108, 176, 0.1) 100%)',
            zIndex: 0,
        },
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.6,
                staggerChildren: 0.1,
            },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 },
    };

    return (
        <>
            <Helmet>
                <title>DNACloudHub | Logout</title>
                <meta name="description" content="You have successfully logged out of DNACloudHub. Securely end your session and return anytime to run qpAdm models on our cloud-based platform.." />
                <meta property="og:title" content={'DNACloudHub | Logout'} />
                <meta property="og:description" content={'"You have successfully logged out of DNACloudHub. Securely end your session and return anytime to run qpAdm models on our cloud-based platform."'} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={`https://dnacloudhub.com/logout`} />
            </Helmet>

            <div style={styles.container}>
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={containerVariants}
                    style={styles.card}
                >
                    {/* Decorative circles */}
                    <div style={{ ...styles.decorativeCircle, width: '300px', height: '300px', top: '-150px', right: '-150px' }} />
                    <div style={{ ...styles.decorativeCircle, width: '200px', height: '200px', bottom: '-100px', left: '-100px' }} />

                    <motion.div variants={itemVariants}>
                        <div style={styles.iconContainer}>
                            <FaSignOutAlt style={styles.icon} />
                        </div>
                    </motion.div>

                    <motion.h1 variants={itemVariants} style={styles.title}>
                        Logged Out Successfully
                    </motion.h1>

                    <motion.p variants={itemVariants} style={styles.message}>
                        Thank you for using DNACloudHub! Your session has been securely ended.
                        We look forward to seeing you again soon.
                    </motion.p>

                    <motion.div variants={itemVariants} style={styles.buttonContainer}>
                        <Link to="/login" style={{ textDecoration: 'none' }}>
                            <Button
                                style={{ ...styles.button, ...styles.primaryButton }}
                                className="d-flex align-items-center"
                            >
                                <FaSignInAlt /> Sign In Again
                            </Button>
                        </Link>
                        <Link to="/" style={{ textDecoration: 'none' }}>
                            <Button
                                style={{ ...styles.button, ...styles.secondaryButton }}
                                className="d-flex align-items-center"
                            >
                                <FaHome /> Home
                            </Button>
                        </Link>
                    </motion.div>
                </motion.div>
            </div>
        </>
    );
}

export default Logout;
